import React, { Children, memo } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Utensils, GlassWater, ShoppingBag, ChefHat, Package } from 'lucide-react';
export function ConceptsSection() {
  const concepts = [{
    title: 'Restaurants',
    description: 'Immersive dining experiences blending Central Asian heritage with modern culinary techniques.',
    icon: <Utensils size={32} strokeWidth={1.5} data-id="element-72" />,
    image: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?w=800&q=80',
    href: '/concepts/restaurants'
  }, {
    title: 'Private Dining & Events',
    description: 'Elegant spaces and bespoke menus for your most memorable celebrations and corporate gatherings.',
    icon: <GlassWater size={32} strokeWidth={1.5} data-id="element-73" />,
    image: 'https://images.unsplash.com/photo-1414235077428-338989a2e8c0?w=800&q=80',
    href: '/concepts/private-dining'
  }, {
    title: 'Grab & Go',
    description: 'Artisan quality meals and pastries crafted daily for those seeking excellence on the move.',
    icon: <ShoppingBag size={32} strokeWidth={1.5} data-id="element-74" />,
    image: 'https://images.unsplash.com/photo-1509440159596-0249088772ff?w=800&q=80',
    href: '/concepts/grab-and-go'
  }, {
    title: 'Commissary & Catering',
    description: 'Large-scale culinary operations delivering consistent quality for off-site events and wholesale.',
    icon: <ChefHat size={32} strokeWidth={1.5} data-id="element-75" />,
    image: 'https://images.unsplash.com/photo-1555244162-803834f70033?w=800&q=80',
    href: '/concepts/catering'
  }, {
    title: 'Ready-to-Cook',
    description: 'Premium marinated meats and prepared ingredients to elevate your home cooking experience.',
    icon: <Package size={32} strokeWidth={1.5} data-id="element-76" />,
    image: 'https://images.unsplash.com/photo-1466637574441-749b8f19452f?w=800&q=80',
    href: '/concepts/ready-to-cook'
  }];
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: 40
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut'
      }
    }
  };
  return <section id="concepts" className="py-32 md:py-40 bg-white relative" data-id="element-77">
      {/* Noise Overlay */}
      <div className="absolute inset-0 bg-noise pointer-events-none z-20 opacity-30" data-id="element-78"></div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10" data-id="element-79">
        <div className="text-center mb-20 md:mb-32" data-id="element-80">
          <motion.h2 className="text-5xl md:text-6xl font-serif text-brand-dark mb-6" initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-81">
            Our Concepts
          </motion.h2>
          <motion.div className="w-24 h-1 bg-brand-red mx-auto" initial={{
          opacity: 0,
          scaleX: 0
        }} whileInView={{
          opacity: 1,
          scaleX: 1
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} data-id="element-82"></motion.div>
        </div>

        <motion.div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-6" variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: '-50px'
      }} data-id="element-83">
          {concepts.map((concept, index) => {
          // First 3 cards take 2 columns each (3 per row)
          // Last 2 cards take 3 columns each (2 per row)
          const colSpan = index < 3 ? 'lg:col-span-2' : 'lg:col-span-3';
          const aspect = index < 3 ? 'aspect-[4/5]' : 'aspect-[16/9] lg:aspect-[3/2]';
          return <motion.div key={index} variants={cardVariants} className={`group relative overflow-hidden bg-brand-dark rounded-sm cursor-pointer ${colSpan}`} whileHover={{
            y: -8
          }} transition={{
            type: 'spring',
            stiffness: 300,
            damping: 20
          }} data-id="element-84">
                <Link to={concept.href} className="absolute inset-0 z-30" data-id="element-85" />
                <div className="absolute inset-0 bg-brand-red/90 opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-10 flex flex-col justify-center items-center text-center p-8 md:p-12 backdrop-blur-sm" data-id="element-86">
                  <div className="text-brand-gold mb-6 transform translate-y-8 group-hover:translate-y-0 transition-transform duration-500 ease-out" data-id="element-87">
                    {concept.icon}
                  </div>
                  <p className="text-white/90 font-light text-lg transform translate-y-8 group-hover:translate-y-0 transition-transform duration-500 delay-75 ease-out max-w-sm" data-id="element-88">
                    {concept.description}
                  </p>
                  <span className="mt-8 border border-brand-gold text-brand-gold px-8 py-3 text-sm uppercase tracking-[0.2em] hover:bg-brand-gold hover:text-brand-dark transition-colors duration-300 transform translate-y-8 group-hover:translate-y-0 delay-150 ease-out" data-id="element-89">
                    Explore
                  </span>
                </div>

                <div className={`${aspect} relative`} data-id="element-90">
                  <div className="absolute inset-0 bg-gradient-to-t from-brand-dark via-brand-dark/40 to-transparent z-0" data-id="element-91"></div>
                  <img src={concept.image} alt={concept.title} className="w-full h-full object-cover group-hover:scale-110 group-hover:rotate-1 transition-transform duration-1000 ease-out opacity-80 group-hover:opacity-40" data-id="element-92" />

                  {/* Large Number Indicator */}
                  <div className="absolute top-6 left-6 z-0 text-white/20 font-serif text-6xl md:text-8xl font-bold select-none transition-opacity duration-500 group-hover:opacity-0" data-id="element-93">
                    0{index + 1}
                  </div>

                  <div className="absolute bottom-0 left-0 right-0 p-8 md:p-10 z-0" data-id="element-94">
                    <h3 className="text-3xl font-serif text-white mb-4 relative inline-block" data-id="element-95">
                      {concept.title}
                      <span className="absolute -bottom-2 left-0 w-0 h-0.5 bg-brand-gold transition-all duration-500 group-hover:w-full" data-id="element-96"></span>
                    </h3>
                  </div>
                </div>
              </motion.div>;
        })}
        </motion.div>
      </div>
    </section>;
}